import React, { useState, Fragment, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import _xor from 'lodash/xor';
import _orderBy from 'lodash/orderBy';
import { FieldArray, Field, useFormikContext } from 'formik';

import { Icon } from '@pwc/pwc-deals-ui';
import { CommentTarget } from '@pwc/pwc-deals-web-common';
import { wrapWithCommentingWrapper } from 'features/project/ViewProject/ProjectOverview/utils/wrapWithCommentingWrapper';
import { useTranslation } from 'react-i18next';
import { getTranslationForDefaultTag } from 'common/services/tagsI18n';
import Separator from 'common/components/Separator';
import ClearableDisplayPill from 'common/components/Pills/ClearableDisplayPill';
import HiddenInput from 'common/components/HiddenInput/HiddenInput';
import MultiSelectSearch from 'common/components/MultiSelectSearch/MultiSelectSearch';
import { useClickedOutsideOfRef } from 'common/hooks/useClickedOutsideOfRef';
import ErrorMessage from 'common/components/ErrorMessage/ErrorMessage';
import { fetchAllTags } from '../../../analyses/analysisSlice';
import ValidateTag from '../../ValidateTag';
import { getAllTags } from '../../tagService';

import './TagEditor.scss';

const TAGS_FIELD_NAME = 'tags';

const TagEditor = ({
  projectId,
  onSelect,
  tagsRight,
  title,
  subtitle,
  allowCreateTags,
  placeholderText,
  isCommentingAllowed,
  showSeparator = false,
}) => {
  const [fetchedTagOptions, setFetchedTagOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTagsText, setSelectedOrderedTagsText] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const setSelectedTagsText = (tagsList = []) => {
    const orderedList = _orderBy(
      tagsList,
      [(tag) => tag.toLowerCase()],
      ['asc']
    );
    setSelectedOrderedTagsText(orderedList);
  };

  const { getFieldMeta, setFieldValue } = useFormikContext();

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const setTagFormValues = useCallback(
    (tags) => {
      setFieldValue(TAGS_FIELD_NAME, tags);
    },
    [setFieldValue]
  );

  const getTagFormValue = useCallback(
    () => getFieldMeta(TAGS_FIELD_NAME).value,
    [getFieldMeta]
  );

  useEffect(() => {
    const tagsFieldValue = getTagFormValue();
    setSelectedTagsText([...(tagsFieldValue || [])]);
  }, [getTagFormValue]);

  useEffect(() => {
    let isMounted = true;

    if (dropdownOpen && loading === true) {
      const fetch = async () => {
        try {
          setError(null);
          const response = await getAllTags(projectId);

          if (isMounted) {
            const tagsWithDisplayText = response.map((tagObj) => ({
              ...tagObj,
              displayText: getTranslationForDefaultTag(tagObj?.text, t),
            }));
            setFetchedTagOptions(tagsWithDisplayText);
            dispatch(fetchAllTags({ tags: response }));
            setLoading(false);
          }
        } catch (e) {
          dispatch(fetchAllTags({ tags: [] }));
          setLoading(false);
          setError('An error occurred while fetching data');
          setDropdownOpen(false);
        }
      };
      fetch();
    }

    return () => {
      isMounted = false;
    };
  }, [projectId, dropdownOpen, loading, dispatch, t]);

  useEffect(() => {
    const fetchedTagOptionsByText = fetchedTagOptions.reduce(
      (opts, opt) => ({ ...opts, [opt.text]: opt }),
      {}
    );

    const newTagOptions = selectedTagsText
      .filter((text) => !fetchedTagOptionsByText[text])
      .map((text) => ({ text, displayText: text }));

    setTagOptions([...fetchedTagOptions, ...newTagOptions]);
  }, [fetchedTagOptions, selectedTagsText]);

  const updateSelectedTags = (updatedSelectedTagTexts) => {
    const errors = updatedSelectedTagTexts.map(ValidateTag);
    const hasError = errors.some((e) => !!e);

    if (hasError) {
      return setError(errors);
    }
    const validatedUpdatedSelectedTagTexts = errors
      .map((err, i) => (err ? null : updatedSelectedTagTexts[i]))
      .flat();
    setSelectedTagsText(validatedUpdatedSelectedTagTexts);
    setTagFormValues(validatedUpdatedSelectedTagTexts);
    setError('');
    setSearch('');
    return validatedUpdatedSelectedTagTexts;
  };

  const removeTag = (tagIndex) => {
    const updatedSelectedTagTexts = [
      ...selectedTagsText.slice(0, tagIndex),
      ...selectedTagsText.slice(tagIndex + 1),
    ];
    setSelectedTagsText(updatedSelectedTagTexts);
    setTagFormValues(updatedSelectedTagTexts);

    if (onSelect) {
      onSelect(
        updatedSelectedTagTexts,
        !!_xor(getTagFormValue(), updatedSelectedTagTexts)
      );
    }
  };

  const handleEscapeKey = (ev) => {
    if (ev.keyCode === 27) {
      setDropdownOpen(false);
    }
  };

  const onSubmit = (updatedSelectedTagTexts) => {
    setDropdownOpen(false);
    const validatedUpdatedSelectedTagTexts = updateSelectedTags(
      updatedSelectedTagTexts
    );

    if (onSelect) {
      onSelect(
        validatedUpdatedSelectedTagTexts,
        !!_xor(getTagFormValue(), validatedUpdatedSelectedTagTexts)
      );
    }
  };

  const ref = useClickedOutsideOfRef(
    (wasClickedOutside) =>
      wasClickedOutside && setDropdownOpen(!wasClickedOutside)
  );

  return (
    <div className="tageditor">
      {title &&
        wrapWithCommentingWrapper(
          <div className="title">
            {}
            <Icon
              name="tag"
              className="fill"
              width={18}
              data-testid="draft-icon"
            />
            {t('tagsOptional')}
          </div>,
          CommentTarget.Tags,
          isCommentingAllowed
        )}

      {subtitle && (
        <div className="description">
          <Icon
            name="information"
            className="fill icon-yellow"
            width={18}
            data-testid="draft-icon"
          />
          {t('tagContentByReport')}
        </div>
      )}

      {showSeparator && <Separator />}
      <FieldArray
        name="tags"
        render={(arrayHelpers) => (
          <>
            {error ? <ErrorMessage msg={error} /> : ''}
            <div
              className="input-field-container"
              data-tags-right={`${tagsRight}`}
              ref={ref}
            >
              <span className="filter-search-icon">
                <Icon name="search" width={18} className="outline icon-grey" />
              </span>
              <Field
                id="tag-filter-search"
                name="tag"
                className={`input-field ${dropdownOpen ? 'active' : ''}`}
                placeholder={t(placeholderText)}
                error={error}
                autoComplete="off"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                data-testid="text-input-tag-search"
                onFocus={() => {
                  setLoading(true);
                  setDropdownOpen(true);
                }}
                onKeyDown={(e) => handleEscapeKey(e)}
              />

              {dropdownOpen && (
                <MultiSelectSearch
                  isLoading={loading}
                  searchCriteria={search}
                  options={tagOptions}
                  onSubmit={(updatedSelectedTagTexts) =>
                    onSubmit(updatedSelectedTagTexts, arrayHelpers)
                  }
                  selectedTags={selectedTagsText}
                  allowCreateTags={allowCreateTags}
                  innerIdWhitelist={['tag-filter-search']}
                  closeDropdown={() => setDropdownOpen(false)}
                />
              )}
            </div>
            {selectedTagsText && (
              <div className="pills" data-tags-right={`${tagsRight}`}>
                {selectedTagsText.map((p, index) => (
                  <Fragment key={p}>
                    <Field name={`tag[${index}]`} component={HiddenInput} />
                    <ClearableDisplayPill
                      onClearClick={() => removeTag(index, arrayHelpers)}
                      tag={getTranslationForDefaultTag(p, t)}
                      index={index}
                    />
                  </Fragment>
                ))}
              </div>
            )}
          </>
        )}
      />
    </div>
  );
};

TagEditor.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onSelect: PropTypes.func,
  tagsRight: PropTypes.bool,
  title: PropTypes.bool,
  subtitle: PropTypes.bool,
  allowCreateTags: PropTypes.bool,
  placeholderText: PropTypes.string,
};

TagEditor.defaultProps = {
  onSelect: undefined,
  tagsRight: false,
  title: true,
  subtitle: true,
  allowCreateTags: true,
  placeholderText: 'searchOrCreateANewTag',
};

export default TagEditor;
